import { useState } from 'react'
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import {
  PAYMENT_CATEGORY_CODE,
  PAYMENT_CATEGORY_NAME_MAX,
  PAYMENT_CATEGORY_PRIORITY_MAX,
  REVENUE_KINDS,
  createPaymentCategory,
  fetchPaymentCategories,
  paymentCategoryName,
  updatePaymentCategory,
  type PaymentCategory,
  type PaymentCategoryDraft,
  type RevenueKind,
} from '@/features/cashdesk/paymentCategoryApi'

interface CategoryForm {
  code: string
  name: string
  priority: number | null
  revenueKind?: RevenueKind
  active: boolean
}

export function PaymentCategoriesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<CategoryForm>()
  const [editing, setEditing] = useState<PaymentCategory | 'new' | null>(null)
  const current = editing !== null && editing !== 'new' ? editing : null
  const locked = current?.system ?? false

  const categories = useQuery({
    queryKey: queryKeys.paymentCategories,
    queryFn: ({ signal }) => fetchPaymentCategories(signal),
  })

  const save = useMutation({
    mutationFn: (values: CategoryForm) => {
      const draft: PaymentCategoryDraft = {
        code: current?.code ?? values.code.trim(),
        name: values.name.trim(),
        priority: values.priority ?? 0,
        revenueKind: values.revenueKind ?? 'OTHER',
        active: values.active,
      }
      return current ? updatePaymentCategory(current.id, draft) : createPaymentCategory(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentCategories })
      setEditing(null)
    },
  })

  const open = (target: PaymentCategory | 'new') => {
    save.reset()
    setEditing(target)
    const nextPriority = Math.min(
      PAYMENT_CATEGORY_PRIORITY_MAX,
      Math.max(0, ...(categories.data ?? []).map((c) => c.priority)) + 1,
    )
    form.setFieldsValue(
      target === 'new'
        ? { code: '', name: '', priority: nextPriority, revenueKind: undefined, active: true }
        : {
            code: target.code,
            name: target.name,
            priority: target.priority,
            revenueKind: target.revenueKind,
            active: target.active,
          },
    )
  }

  const columns: ColumnsType<PaymentCategory> = [
    { key: 'priority', title: t('paymentCategories.priority'), width: 110, align: 'right', render: (_, row) => row.priority },
    {
      key: 'name',
      title: t('paymentCategories.name'),
      render: (_, row) => (
        <Space size={6} wrap>
          <span>{paymentCategoryName(row, t)}</span>
          {row.system ? <Tag>{t('paymentCategories.system')}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'code',
      title: t('paymentCategories.code'),
      width: 180,
      render: (_, row) => <Typography.Text code>{row.code}</Typography.Text>,
    },
    {
      key: 'revenueKind',
      title: t('paymentCategories.revenueKindLabel'),
      width: 220,
      render: (_, row) => t(`paymentCategories.revenueKind.${row.revenueKind}`),
    },
    {
      key: 'state',
      title: t('paymentCategories.state'),
      width: 130,
      render: (_, row) =>
        row.active ? (
          <Tag color="green">{t('paymentCategories.active')}</Tag>
        ) : (
          <Tag>{t('paymentCategories.inactive')}</Tag>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[{ key: 'edit', label: t('common.edit'), icon: <EditOutlined />, onClick: () => open(row) }]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.paymentCategories')}
        description={t('pageIntro.paymentCategories')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            {t('paymentCategories.add')}
          </Button>
        }
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('paymentCategories.rule')} />
        {categories.isError ? <Alert type="error" showIcon message={translateError(categories.error, t)} /> : null}
        <Table<PaymentCategory>
          rowKey={(row) => row.id}
          loading={categories.isPending}
          dataSource={categories.data ?? []}
          columns={columns}
          pagination={false}
          scroll={{ x: 800 }}
          locale={{ emptyText: t('paymentCategories.empty') }}
        />
      </Space>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? t('paymentCategories.addTitle') : t('paymentCategories.editTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={save.isPending}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
          {locked ? <Alert type="info" showIcon message={t('paymentCategories.systemLocked')} /> : null}
          <Form<CategoryForm> form={form} layout="vertical" requiredMark preserve={false}>
            <Form.Item
              name="code"
              label={t('paymentCategories.code')}
              extra={current ? undefined : t('paymentCategories.codeHint')}
              normalize={(value: string) => value.toUpperCase()}
              rules={[
                { required: true, whitespace: true, message: t('paymentCategories.codeRequired') },
                { pattern: PAYMENT_CATEGORY_CODE, message: t('paymentCategories.codeInvalid') },
              ]}
            >
              <Input maxLength={32} disabled={current !== null} />
            </Form.Item>
            <Form.Item
              name="name"
              label={t('paymentCategories.name')}
              rules={[{ required: true, whitespace: true, message: t('paymentCategories.nameRequired') }]}
            >
              <Input maxLength={PAYMENT_CATEGORY_NAME_MAX} />
            </Form.Item>
            <Form.Item
              name="priority"
              label={t('paymentCategories.priority')}
              extra={t('paymentCategories.priorityHint')}
              rules={[{ required: true, message: t('paymentCategories.priorityRequired') }]}
            >
              <InputNumber min={1} max={PAYMENT_CATEGORY_PRIORITY_MAX} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="revenueKind"
              label={t('paymentCategories.revenueKindLabel')}
              rules={[{ required: true, message: t('paymentCategories.revenueKindRequired') }]}
            >
              <Select<RevenueKind>
                disabled={locked}
                options={REVENUE_KINDS.map((value) => ({
                  value,
                  label: t(`paymentCategories.revenueKind.${value}`),
                }))}
              />
            </Form.Item>
            <Form.Item name="active" label={t('paymentCategories.active')} valuePropName="checked">
              <Switch disabled={locked} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  )
}
